import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import deleteIcon from '../../asset/icon/delete.png';
import belowIcon from '../../asset/icon/below.png';
import { searchIndividualMember } from '../../shared/api/memberApi';
import {
  BlackFullWidthBtn,
  PaperBlurFitBtn,
} from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import {
  Container,
  Header,
  MobileHeader,
  ResultDetail,
  ResultName,
  SearchInput,
  SearchMessage,
  SearchResult,
  SearchResults,
  Title,
} from '../../shared/component/modal/SearchModalLayout';
import { getBtnName } from '../../shared/constants/btnNames';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { MemberSearchData } from '../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  ApprovalRequestStatus,
  ApprovalRequestType,
} from '../../shared/enum/EnumRepository';
import {
  useApprovalRequestActions,
  useApprovalRequestState,
} from '../../shared/hooks/useApi/useApprovalRequest';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';

interface GroupMemberInviteModalProps {
  isMobile?: boolean;
  setIsUserInput: (isUserInput: boolean) => void;
}

const GroupMemberInviteModal: React.FC<GroupMemberInviteModalProps> = ({
  isMobile = false,
  setIsUserInput,
}) => {
  const { standardModal, clearModal } = useModalStore();
  const groupMemberId = standardModal?.value;
  const numericGroupMemberId = Number(groupMemberId);
  const groupMemberExists = useGroupMemberStore((state) =>
    state.groupMemberList.some((member) => member.id === groupMemberId));
  const { approvalRequestList } = useApprovalRequestState();
  const { createGroupMemberConnectionApprovalRequest } = useApprovalRequestActions();
  const { enqueueAlert } = useStandardAlertStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchData[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberSearchData | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchAttempt, setSearchAttempt] = useState(0);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteFailed, setInviteFailed] = useState(false);
  const latestSearchId = useRef(0);

  const pendingInviteeAuis = useMemo(() => new Set(
    approvalRequestList
      .filter((request) =>
        request.type === ApprovalRequestType.GROUP_MEMBER_CONNECTION
        && request.status === ApprovalRequestStatus.PENDING)
      .map((request) => request.approver.aui),
  ), [approvalRequestList]);

  useEffect(() => {
    setIsUserInput(query.length > 0);
  }, [query, setIsUserInput]);

  useEffect(() => () => setIsUserInput(false), [setIsUserInput]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchIndividualMember(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query, searchAttempt]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal(isMobile ? 500 : undefined);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal, isMobile]);

  if (
    !standardModal
    || !groupMemberId
    || !Number.isSafeInteger(numericGroupMemberId)
    || numericGroupMemberId <= 0
    || !groupMemberExists
  ) return null;

  const handleClose = () => {
    clearModal(isMobile ? 500 : undefined);
  };

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setSelectedMember(null);
    setInviteFailed(false);
  };

  const handleInvite = async () => {
    if (isInviting) return;
    if (!selectedMember) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_ACCOUNT_TO_INVITE'),
      });
      return;
    }
    if (pendingInviteeAuis.has(selectedMember.aui)) return;

    setIsInviting(true);
    setInviteFailed(false);
    try {
      const invited = await createGroupMemberConnectionApprovalRequest({
        groupMemberId: numericGroupMemberId,
        approverAui: selectedMember.aui,
      });
      if (invited) {
        handleClose();
        return;
      }
      setInviteFailed(true);
    } catch {
      setInviteFailed(true);
    } finally {
      setIsInviting(false);
    }
  };

  const ModalHeader = isMobile ? MobileHeader : Header;
  return (
    <InviteContainer $isMobile={isMobile} aria-busy={isInviting}>
      <ModalHeader>
        <Title>{getModalMessage('GROUP_MEMBER_INVITE_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          hoverOpacity={!isMobile}
          backgroundImg={isMobile ? belowIcon : deleteIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </ModalHeader>
      <SearchInput
        value={query}
        onChange={handleQueryChange}
        placeholder={getModalMessage('GROUP_MEMBER_INVITE_SEARCH_PLACEHOLDER')}
        aria-label={getModalMessage('GROUP_MEMBER_INVITE_SEARCH_PLACEHOLDER')}
        autoFocus
      />
      <InviteSearchResults $isMobile={isMobile}>
        {isSearching && (
          <SearchMessage>
            {getModalMessage('GROUP_MEMBER_INVITE_SEARCHING')}
          </SearchMessage>
        )}
        {!isSearching && searchFailed && (
          <SearchFailure role="alert">
            <span>{getModalMessage('GROUP_MEMBER_INVITE_SEARCH_FAILED')}</span>
            <SearchRetryButton onClick={() => setSearchAttempt((value) => value + 1)}>
              {getBtnName('RETRY')}
            </SearchRetryButton>
          </SearchFailure>
        )}
        {!isSearching && !searchFailed && !query.trim() && (
          <SearchMessage>{getModalMessage('GROUP_MEMBER_INVITE_EMPTY')}</SearchMessage>
        )}
        {!isSearching && !searchFailed && query.trim() && results.length === 0 && (
          <SearchMessage>
            {getModalMessage('GROUP_MEMBER_INVITE_NO_RESULTS')}
          </SearchMessage>
        )}
        {!isSearching && results.map((member) => {
          const isPending = pendingInviteeAuis.has(member.aui);
          const isSelected = selectedMember?.aui === member.aui;
          return (
            <InviteSearchResult
              key={member.aui}
              type="button"
              disabled={isPending}
              $isSelected={isSelected}
              onClick={() => {
                setSelectedMember(member);
                setInviteFailed(false);
              }}
              aria-pressed={isSelected}
            >
              <ResultName>{member.username}</ResultName>
              <ResultMeta>
                <ResultDetail>@{member.handle}</ResultDetail>
                {isPending && (
                  <PendingLabel>
                    {getModalMessage('GROUP_MEMBER_INVITE_ALREADY_PENDING')}
                  </PendingLabel>
                )}
              </ResultMeta>
            </InviteSearchResult>
          );
        })}
      </InviteSearchResults>
      {inviteFailed && (
        <InviteError role="alert">
          {getModalMessage('GROUP_MEMBER_INVITE_FAILED')}
        </InviteError>
      )}
      <InviteButton
        onClick={handleInvite}
        disabled={isInviting}
      >
        {getBtnName(isInviting ? 'INVITING' : 'INVITE')}
      </InviteButton>
    </InviteContainer>
  );
};

export default GroupMemberInviteModal;

const InviteContainer = styled(Container)<{ $isMobile: boolean }>`
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  ${({ $isMobile }) => $isMobile && `
    padding: 0;
    height: min(72dvh, 620px);
    max-height: calc(100dvh - 24px);
  `}
`;

const InviteSearchResults = styled(SearchResults)<{ $isMobile: boolean }>`
  ${({ $isMobile }) => $isMobile && `
    min-height: 0;
    max-height: none;
    flex: 1;
  `}
`;

const InviteSearchResult = styled(SearchResult)<{ $isSelected: boolean }>`
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.color_Gray_07 : 'transparent'};

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

const ResultMeta = styled.span`
  min-width: 0;
  max-width: 55%;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  ${ResultDetail} {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PendingLabel = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const InviteError = styled.div`
  min-height: 18px;
  padding-top: 8px;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

const SearchFailure = styled(SearchMessage)`
  flex-direction: column;
  gap: 10px;
`;

const SearchRetryButton = styled(PaperBlurFitBtn)`
  padding: 6px 10px;
`;

const InviteButton = styled(BlackFullWidthBtn)`
  margin-top: 18px;
`;
